import { useEffect, useState } from "react";
import { ADDRESS_GEN_TYPE } from "../../../../constants/config.constants";
import { useUpdateCoordsMutation, useValidateCoordsMutation } from "../../../../features/patient/patientApiSlice";
import ReusableModal from "../../../../components/Modals/ReusableModal";
import { Alert, Snackbar, Tooltip, CircularProgress } from "@mui/material";
import LanguageIcon from '@mui/icons-material/Language';

const LatLongDisplay = ({
    latitude,
    longitude,
    loading = false,
    editable = true,
    address,
    patientId,
    onAddressUpdate,
    isFormDispatch,
    disabled = false
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editCoordinates, setEditCoordinates] = useState({ lat: '', lng: '' });
    const ADDRESS_GEN_LABELS = {
        [ADDRESS_GEN_TYPE.AUTO]: 'Auto-generated from address',
        [ADDRESS_GEN_TYPE.MANUAL]: 'Manually entered by authorized user',
        [ADDRESS_GEN_TYPE.RELATIVE]: (city, state) =>
            `Relative coordinates based on ${city && state ? `${city}, ${state}` : 'city, state'}`,
    };

    const [validateCoordinates] = useValidateCoordsMutation();
    const [updateCoordinates] = useUpdateCoordsMutation();
    const [modal, setModal] = useState({ open: false, message: "", confirmAction: null });
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("Something went wrong");
    const [severity, setSeverity] = useState("error");

    const [isValidating, setIsValidating] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    // Initialize edit coordinates when entering edit mode
    const handleEdit = () => {
        setEditCoordinates({
            lat: latitude || '',
            lng: longitude || ''
        });
        setIsEditing(true);
    };
    // Handle save
    // const handleSave = () => {
    //     if (onSave) {
    //         onSave(editCoordinates);
    //     }
    //     setIsEditing(false);
    // };

    // Handle cancel
    const handleCancel = () => {
        setEditCoordinates({ lat: latitude || '', lng: longitude || '' });
        setIsEditing(false);
    };

    const isValidCoordinate = (lat, lng) => {
        const latNum = parseFloat(lat);
        const lngNum = parseFloat(lng);
        return (
            !isNaN(latNum) &&
            !isNaN(lngNum) &&
            latNum >= -90 &&
            latNum <= 90 &&
            lngNum >= -180 &&
            lngNum <= 180
        );
    };


    const isValidDecimalPlaces = (value, maxDecimals = 8) => {
        const regex = new RegExp(`^-?\\d+(\\.\\d{1,${maxDecimals}})?$`);
        return regex.test(value);
    };

    const showAlert = (msg, severity) => {
        setOpen(true);
        setMessage(msg);
        setSeverity(severity);
    };


    const handleSave = async (force) => {
        const { lat, lng } = editCoordinates;


        //  Check if user actually changed values
        if (
            parseFloat(lat) === parseFloat(latitude) &&
            parseFloat(lng) === parseFloat(longitude)
        ) {
            showAlert("No changes detected in coordinates.", "info");
            return;
        }
        if (!isValidDecimalPlaces(lat) || !isValidDecimalPlaces(lng)) {
            showAlert(`Coordinates can have between 6 to 8 decimal places.`, "warning");
            return;
        }

        if (!isValidCoordinate(lat, lng)) {
            showAlert("Invalid coordinates. Latitude must be between -90 and 90, and longitude between -180 and 180.", "warning");
            return;
        }

        const latNum = parseFloat(lat);
        const lngNum = parseFloat(lng);
        const oldLatNum = parseFloat(latitude);
        const oldLngNum = parseFloat(longitude);
        setIsValidating(true);

        try {
            // 1️⃣ Validate with backend
            const result = await validateCoordinates({
                locationId: address?.id,
                newLat: latNum,
                newLng: lngNum,
            }).unwrap();
            setIsValidating(false);
            // 2️⃣ If status === false → show modal warning
            if (result?.data?.status === false && !force) {
                setModal({
                    open: true,
                    message:
                        result?.data?.message ||
                        "The new coordinates differ significantly or appear to be in another state. Do you want to continue?",
                    confirmAction: async () => {
                        // ✅ User confirmed — proceed with updating coordinates
                        // if (onSave) onSave({ lat: latNum, lng: lngNum });
                        setIsUpdating(true);
                        try {
                            const result = await updateCoordinates({
                                patientId,
                                locationId: address?.id,
                                lat: latNum,
                                long: lngNum,
                                type: ADDRESS_GEN_TYPE.MANUAL

                            })?.unwrap();
                            onAddressUpdate?.(result.data.location);
                            setModal({ open: false, message: "", confirmAction: null });
                            setIsEditing(false);
                            showAlert("Coordinates saved successfully", "success");
                            setIsUpdating(false);
                        } catch (err) {
                            showAlert(err?.data?.message);
                            setIsUpdating(false);
                        }

                    },
                });
            } else {
                // if (onSave) onSave({ lat: latNum, lng: lngNum });
                setIsUpdating(true);
                try {
                    const result = await updateCoordinates({
                        patientId,
                        locationId: address?.id,
                        lat: latNum,
                        long: lngNum,
                        type: ADDRESS_GEN_TYPE.MANUAL

                    })?.unwrap();
                    onAddressUpdate?.(result.data.location);
                    // setModal({ open: false, message: "", confirmAction: null });
                    setIsEditing(false);
                    showAlert("Coordinates saved successfully", "success");
                    setIsUpdating(false);
                } catch (err) {
                    showAlert(err?.data?.message);
                    setIsUpdating(false);
                }
                setIsEditing(false);
            }
        } catch (err) {
            showAlert("Failed to validate coordinates. Please try again.");
            setIsValidating(false);
        }
    };


    // Determine save button state and text
    const getSaveButtonState = () => {
        if (isValidating) {
            return {
                text: "Validating...",
                disabled: true,
                loading: true
            };
        }
        if (isUpdating) {
            return {
                text: "Saving...",
                disabled: true,
                loading: true
            };
        }
        return {
            text: "Save",
            disabled: false,
            loading: false
        };
    };

    const saveButtonState = getSaveButtonState();
    useEffect(() => {
        setIsEditing(false);
        // setEditCoordinates({ lat: '', lng: '' });
    }, [address?.id]);
    return (
        <>
            <Snackbar
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                open={open}
                autoHideDuration={5000}
                message={message ? message : "Something went wrong"}
                severity="error"
            >
                <Alert
                    onClose={() => setOpen(false)}
                    severity={severity ? severity : "error"}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {message ? message : "Something went wrong"}
                </Alert>
            </Snackbar>
            {modal.open && (
                <ReusableModal
                    open={modal.open}
                    onClose={() => setModal({ open: false, message: "", confirmAction: null })}
                    title="Coordinates Validation"
                    description={modal.message}
                    confirmLabel={modal.confirmAction ? "Confirm" : "Close"}
                    onConfirm={modal.confirmAction || (() => setModal({ open: false, message: "", confirmAction: null }))}
                    confirmButtonClass={modal.confirmAction ? "bg-red-600 hover:bg-red-700 text-white" : ""}
                >
                    <div className="p-4 flex flex-col gap-1 w-[50vw] max-w-full">
                        <p className="text-gray-800 text-sm leading-relaxed m-0">
                            {modal.message}
                        </p>

                        <div className="flex justify-end gap-2 mt-3">
                            <button
                                onClick={() => setModal({ open: false, message: "", confirmAction: null })}
                                className="px-3 py-1 text-sm text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                            >
                                Cancel
                            </button>

                            {/* {modal.confirmAction && (
                                <button
                                    onClick={() => {
                                        modal.confirmAction();
                                    }}
                                    disabled={saveButtonState.disabled}
                                    className=" px-3 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    save
                                </button>
                            )} */}

                            {modal.confirmAction && (
                                <button
                                    onClick={() => {
                                        setModal({ open: false, message: "", confirmAction: null })
                                        handleSave(true);
                                    }}
                                    disabled={saveButtonState.disabled}
                                    className=" px-3 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    save
                                </button>
                            )}


                        </div>
                    </div>
                </ReusableModal>

            )}
            <div className={`p-3 rounded-md ${isEditing ? 'bg-amber-50 border-[1px] border-amber-400' : 'border bg-slate-100 border-primarygrayborder'}`}>
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <LanguageIcon fontSize="small" className="text-blue-600" />
                        <p className="m-0 text-sm font-semibold text-gray-800">Coordinates</p>
                    </div>
                    {!isEditing && editable && (
                        <button
                        disabled={disabled}
                            onClick={handleEdit}
                            className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Edit
                        </button>
                    )}
                </div>

                {isEditing ? (
                    <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                            <div className="flex-1">
                                <label className="block text-xs text-primarytextgrey mb-1  font-semibold">Latitude</label>
                                <input
                                    type="text"
                                    step="0.000001"
                                    max="90"
                                    min="-90"
                                    value={editCoordinates.lat}
                                    onChange={(e) => setEditCoordinates(prev => ({ ...prev, lat: e.target.value }))}
                                    className="w-full px-2 py-1 text-sm border rounded"
                                    placeholder="Enter latitude"
                                    disabled={isValidating || isUpdating}
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-xs text-primarytextgrey mb-1  font-semibold">Longitude</label>
                                <input
                                    type="text"
                                    step="0.000001"
                                    max="180"
                                    min="-180"
                                    value={editCoordinates.lng}
                                    onChange={(e) => setEditCoordinates(prev => ({ ...prev, lng: e.target.value }))}
                                    className="w-full px-2 py-1 text-sm border rounded"
                                    placeholder="Enter longitude"
                                    disabled={isValidating || isUpdating}
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 mt-2">
                            {/* <button
                                onClick={handleSave}
                                className="flex-1 px-3 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
                            >
                                Save
                            </button> */}
                            <button
                                onClick={() => handleSave(false)}
                                disabled={saveButtonState.disabled}
                                className="flex-1 px-3 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {saveButtonState.loading && <CircularProgress size={16} color="inherit" />}
                                {saveButtonState.text}
                            </button>
                            <button
                                onClick={handleCancel}
                                disabled={isValidating || isUpdating}
                                className="flex-1 px-3 py-1 text-sm text-gray-600 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                        </div>
                        <p className="m-0 text-primarytextgrey text-xs italic">
                            {address?.coord_generation_type === ADDRESS_GEN_TYPE.RELATIVE
                                ? ADDRESS_GEN_LABELS.RELATIVE(address?.city, address?.state)
                                : ADDRESS_GEN_LABELS[address?.coord_generation_type] ?? ''}
                        </p>
                    </div>
                ) : (
                    <div>
                        <div className="flex gap-4">
                            <div className="max-w-[120px]">
                                <p className="text-xs mb-1 text-primarytextgrey  font-semibold">Latitude</p>
                                <p className="text-sm mb-2 font-medium truncate" title={latitude}>
                                    {/* {loading ? <span className="animate-pulse">...</span> : latitude || 'N/A'} */}
                                    {loading ? (
                                        <span className="animate-pulse">...</span>
                                    ) : latitude ? (
                                        (() => {
                                            const str = latitude.toString();
                                            if (str.includes('.')) {
                                                const [intPart, decPart] = str.split('.');
                                                const truncatedDec = decPart.slice(0, 8);
                                                const needsEllipsis = decPart.length > 8;
                                                return `${intPart}.${truncatedDec}${needsEllipsis ? '...' : ''}`;
                                            }
                                            return str.length > 8 ? str.slice(0, 8) + '...' : str;
                                        })()
                                    ) : (
                                        'N/A'
                                    )}
                                </p>
                            </div>
                            <div className="max-w-[120px]">
                                <p className="text-xs mb-1 text-primarytextgrey font-semibold">Longitude</p>
                                <p className="text-sm mb-2 font-medium truncate" title={longitude}>
                                    {loading ? (
                                        <span className="animate-pulse">...</span>
                                    ) : longitude ? (
                                        (() => {
                                            const str = longitude.toString();
                                            if (str.includes('.')) {
                                                const [intPart, decPart] = str.split('.');
                                                const truncatedDec = decPart.slice(0, 8);
                                                const needsEllipsis = decPart.length > 8;
                                                return `${intPart}.${truncatedDec}${needsEllipsis ? '...' : ''}`;
                                            }
                                            return str.length > 8 ? str.slice(0, 8) + '...' : str;
                                        })()
                                    ) : (
                                        'N/A'
                                    )}
                                </p>
                            </div>
                        </div>
                        {isFormDispatch && <p className="m-0 text-primarytextgrey text-xs font-semibold">
                            Coordinates used for distance calculations in search
                        </p>}
                        {!isFormDispatch &&
                            <p className="m-0 text-primarytextgrey text-xs italic">
                                {address?.coord_generation_type === ADDRESS_GEN_TYPE.RELATIVE
                                    ? ADDRESS_GEN_LABELS.RELATIVE(address?.city, address?.state)
                                    : ADDRESS_GEN_LABELS[address?.coord_generation_type] ?? ''}
                            </p>}
                    </div>
                )}
            </div>
        </>
    );
};

export default LatLongDisplay;